import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtIST, errDetail } from "@/lib/api";
import { BackButton } from "@/hooks/useBackNavigation";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common";
import {
  BookOpen, Download, FileWarning, Lock, ExternalLink, Youtube, Loader2,
} from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;

/* Read-only Knowledge document/video viewer (2026-09, Workstream D/E, extended
   by Workstream A) — a dedicated SalesOS page, not the raw file endpoint opened
   directly, so employees always get SalesOS's own chrome (title/category/
   product/department/last-updated + a Download action) and a render mode chosen
   server-side (doc.preview_kind) rather than guessed client-side from a mime
   type.

   Render modes:
     pdf         -> full pdf.js viewer with page/zoom/fit-width/fullscreen
                    controls (PdfViewer.jsx), replacing the old bare iframe
     docx        -> read-only mammoth.js render
     text/image  -> inline
     video       -> HTML5 <video> over the byte-range streaming proxy
     youtube     -> fixed, server-built youtube-nocookie embed
     legacy_doc/other -> explicit Download fallback

   Nothing here ever talks to Google Drive directly or sees a Drive credential:
   bytes come only from the backend-proxied doc.view_url / doc.stream_url, and
   the YouTube embed URL is built server-side (routers_knowledge_employee.
   _for_employee) from a validated 11-character video ID — this page never
   constructs an embed URL from admin-entered text. */

/* pdf.js is ~400KB gzipped and this app has NO route-level code splitting
   (src/App.js imports all ~40 pages eagerly), so an eager import here would
   put the whole PDF engine in the main bundle — downloaded on the login
   page, the pipeline, everywhere — to serve one route. Hence this single
   lazy boundary.

   A lazy boundary was tried earlier and appeared broken: the Suspense
   fallback never resolved and the subtree stayed mounted but hidden with
   `display: none`. That was NOT React.lazy's doing — it was react-pdf's
   <Document> suspending forever via React 19's `use()`. With react-pdf gone
   and pdf.js driven directly (nothing in PdfViewer suspends), the boundary
   resolves normally. Measured: it moves ~400KB out of the main bundle. */
const PdfViewer = lazy(() => import("@/components/PdfViewer"));

const DocxPreview = ({ url }) => {
  const [html, setHtml] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(undefined);
    setError(null);
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`Could not load the document (${res.status})`);
        const arrayBuffer = await res.arrayBuffer();
        const mammoth = await import("mammoth");
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) setHtml(result.value);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not render this document");
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <EmptyState icon={FileWarning} title="Couldn't render a preview" hint={error}
        action={<a href={url} target="_blank" rel="noreferrer"><Button size="sm"><Download className="h-3.5 w-3.5 mr-1.5" />Download instead</Button></a>} />
    );
  }
  if (html === undefined) {
    return <div className="space-y-2 p-6"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-full" /><Skeleton className="h-5 w-1/2" /></div>;
  }
  return (
    <div
      data-testid="kb-viewer-docx-content"
      className="prose prose-sm max-w-none p-6 sm:p-10 [&_table]:border-collapse [&_td]:border [&_td]:p-1.5 [&_th]:border [&_th]:p-1.5"
      // Read-only by construction: mammoth's output is plain formatted
      // HTML (headings/paragraphs/tables/lists/runs) with no script/form/
      // input content and no editing affordance of any kind — this is a
      // rendered snapshot, never a live connection back to the source file.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const TextPreview = ({ url }) => {
  const [text, setText] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    setText(undefined);
    fetch(url, { credentials: "include" }).then((r) => r.text()).then((t) => { if (!cancelled) setText(t); })
      .catch(() => { if (!cancelled) setText(""); });
    return () => { cancelled = true; };
  }, [url]);
  if (text === undefined) return <div className="p-6"><Skeleton className="h-64" /></div>;
  return <pre data-testid="kb-viewer-text-content" className="p-6 sm:p-10 text-sm whitespace-pre-wrap break-words font-mono">{text}</pre>;
};

const fmtDuration = (secs) => {
  if (!secs || !Number.isFinite(secs)) return null;
  const s = Math.floor(secs % 60);
  const m = Math.floor((secs / 60) % 60);
  const h = Math.floor(secs / 3600);
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
};

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

/* Uploaded-video player (Drive-backed, served through the byte-range
   streaming proxy).

   play/pause/seek/volume/fullscreen all come from the browser's native
   `controls`. Playback SPEED does not — no major browser exposes a rate
   control in its default video chrome (Chrome and Safari hide it behind a
   right-click context menu; Firefox behind its own overflow menu), so the
   requirement is genuinely unmet by `controls` alone and a small explicit
   rate selector is added below.

   POSTER: there is no separately stored poster image for a Drive-uploaded
   video — nothing in the upload path generates one (that would need a
   server-side frame grab, i.e. ffmpeg, which this architecture deliberately
   doesn't have). `preload="metadata"` makes the browser fetch just the
   container header and paint the first frame, which is the poster in
   practice, at a cost of a few KB rather than the whole file. An explicit
   poster is still honoured if a record ever carries one.

   NO AUTOPLAY: there is no `autoPlay` attribute anywhere here. Playback is
   always employee-initiated. */
const VideoPreview = ({ url, poster }) => {
  const videoRef = useRef(null);
  const [state, setState] = useState("loading");   // loading | ready | error
  const [duration, setDuration] = useState(null);
  const [rate, setRate] = useState(1);

  const applyRate = (r) => {
    setRate(r);
    if (videoRef.current) videoRef.current.playbackRate = r;
  };

  return (
    <div className="bg-black/5 dark:bg-black/40">
      <div className="p-3 sm:p-6 flex items-center justify-center relative min-h-[200px]">
        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground"
               data-testid="kb-viewer-video-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading video…
          </div>
        )}
        {state === "error" ? (
          <EmptyState
            icon={FileWarning}
            testId="kb-viewer-video-error"
            title="This video couldn't be played"
            hint="The file may have been removed from storage, or your connection dropped. Try reloading, or contact an administrator."
            action={<Button size="sm" onClick={() => { setState("loading"); videoRef.current?.load(); }}>Retry</Button>}
          />
        ) : (
          <video
            ref={videoRef}
            controls
            // metadata only — the list and this page must never pull the
            // whole file down before the employee presses play.
            preload="metadata"
            poster={poster || undefined}
            className={`max-w-full max-h-[75vh] rounded-md ${state === "loading" ? "opacity-0" : ""}`}
            data-testid="kb-viewer-video"
            onLoadedMetadata={(e) => {
              setState("ready");
              setDuration(e.currentTarget.duration);
              e.currentTarget.playbackRate = rate;
            }}
            onError={() => setState("error")}
          >
            <source src={url} />
            Your browser does not support video playback.
          </video>
        )}
      </div>
      {state !== "error" && (
        <div className="flex items-center gap-2 flex-wrap px-3 sm:px-6 pb-3 text-[11px] text-muted-foreground">
          <span className="font-medium">Speed</span>
          <div className="flex gap-0.5 rounded-md border p-0.5 bg-card" data-testid="kb-video-speed">
            {PLAYBACK_RATES.map((r) => (
              <Button
                key={r} type="button" size="sm"
                variant={rate === r ? "default" : "ghost"}
                className="h-6 px-1.5 text-[11px] tabular-nums"
                data-testid={`kb-video-speed-${String(r).replace(".", "-")}`}
                onClick={() => applyRate(r)}
              >
                {r}×
              </Button>
            ))}
          </div>
          {duration && (
            <span className="ml-auto tabular-nums" data-testid="kb-viewer-video-duration">
              Duration {fmtDuration(duration)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/* YouTube-linked video (2026-09, Workstream A).

   SECURITY: `src` is doc.embed_url verbatim — a string the BACKEND built from
   a fixed youtube-nocookie template around a video ID already validated
   against ^[A-Za-z0-9_-]{11}$ (routers_knowledge.extract_youtube_id). This
   component never concatenates, parses or interpolates admin-entered text
   into an iframe src, and there is no path here that could render
   admin-supplied embed markup. No autoplay parameter is set, server-side or
   here.

   The thumbnail is opportunistic: if YouTube's keyless thumbnail URL fails to
   load (video removed, made private, or the pattern changes), onError simply
   hides it — the embed and every piece of metadata still render. Nothing about
   the item's correctness depends on it. */
const YouTubePreview = ({ doc }) => {
  const [loaded, setLoaded] = useState(false);

  if (!doc.embed_url) {
    return (
      <CardContent className="pt-6">
        <EmptyState
          icon={FileWarning} testId="kb-viewer-youtube-error"
          title="This YouTube item has no valid video link"
          hint="An administrator needs to re-add the YouTube link for this item."
        />
      </CardContent>
    );
  }

  return (
    <div className="bg-black/5 dark:bg-black/40">
      <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground"
               data-testid="kb-viewer-youtube-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading YouTube player…
          </div>
        )}
        <iframe
          key={doc.embed_url}
          data-testid="kb-viewer-youtube"
          src={doc.embed_url}
          title={doc.title}
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
          onLoad={() => setLoaded(true)}
          // Deliberately NOT including "autoplay" in allow — playback is
          // always employee-initiated.
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
      {doc.watch_url && (
        <div className="px-3 sm:px-6 py-2 flex items-center gap-2 text-[11px] text-muted-foreground">
          <Youtube className="h-3.5 w-3.5" />
          Hosted on YouTube — nothing is stored in SalesOS for this item.
          <a href={doc.watch_url} target="_blank" rel="noreferrer" className="ml-auto shrink-0">
            <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid="kb-viewer-youtube-watch">
              <ExternalLink className="h-3 w-3 mr-1" />Watch on YouTube
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};

export default function KnowledgeViewerPage() {
  const { documentId } = useParams();
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setDoc(undefined);
    setError(null);
    api.get(`/knowledge/documents/${documentId}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(errDetail(e, e?.response?.status === 403
        ? "You don't have access to this document" : "Document not found")));
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, [documentId]);

  const productName = doc && products.find((p) => p.code === doc.product_code)?.name;
  // doc.view_url / doc.stream_url already include the /api prefix (set
  // server-side in routers_knowledge_employee._for_employee) — do not
  // prepend it again here. A YouTube item has NEITHER (nothing is stored on
  // SalesOS's side), so fileUrl is correctly null for one.
  const fileUrl = doc && (doc.view_url || doc.stream_url)
    ? `${backend}${doc.view_url || doc.stream_url}`
    : null;

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]/40 flex flex-col" data-testid="knowledge-viewer-page">
      <header className="h-14 border-b bg-card/90 backdrop-blur sticky top-0 z-10 flex items-center gap-3 px-4">
        {/* Shared Back control (2026-09) — return_to (set by the Knowledge
            Base list's own filtered/search state) takes priority, then
            real app history, then this module fallback. A directly-opened
            or new-tab URL correctly lands back on a fresh Knowledge Base
            list rather than losing the user in a dead end. */}
        <BackButton fallbackRoute="/knowledge-base" testId="kb-viewer-back" label="Knowledge Base" className="h-8" />
        <span className="text-muted-foreground/50">|</span>
        <span className="font-heading text-sm font-semibold flex items-center gap-1.5 truncate">
          <BookOpen className="h-4 w-4 shrink-0" />{doc ? doc.title : "Loading…"}
        </span>
        {/* Download stays a SEPARATE, explicit action — opening a document
            never downloads it. A YouTube item has no view_url, so it
            correctly offers no Download (there is nothing to download). */}
        {doc?.view_url && (
          <a href={fileUrl} target="_blank" rel="noreferrer" className="ml-auto shrink-0">
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="kb-viewer-download">
              <Download className="h-3.5 w-3.5 mr-1.5" />Download
            </Button>
          </a>
        )}
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto py-4 sm:py-6 px-3 sm:px-6">
        {error ? (
          <Card><CardContent className="pt-6">
            <EmptyState icon={error.toLowerCase().includes("access") ? Lock : FileWarning}
              title={error} hint="If you believe this is a mistake, contact an administrator." />
          </CardContent></Card>
        ) : doc === undefined ? (
          <Card><CardContent className="pt-6 space-y-2">
            <Skeleton className="h-6 w-1/3" /><Skeleton className="h-40 w-full" />
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            <Card>
              <CardContent className="pt-4 flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="text-[10px]">{doc.category}</Badge>
                {productName && <Badge variant="outline" className="text-[10px]">{productName}</Badge>}
                {doc.department && <Badge variant="outline" className="text-[10px]">{doc.department}</Badge>}
                <span className="text-[11px] text-muted-foreground ml-auto">
                  Last updated {fmtIST(doc.updated_at, { year: true, time: false })}
                  {doc.uploaded_by_name && ` · maintained by ${doc.uploaded_by_name}`}
                </span>
              </CardContent>
            </Card>

            <Card className="overflow-hidden">
              {doc.preview_kind === "pdf" && (
                <Suspense fallback={
                  <div className="p-6 space-y-2" data-testid="kb-pdf-chunk-loading">
                    <Skeleton className="h-8 w-1/2" /><Skeleton className="h-[60vh] w-full" />
                  </div>
                }>
                  <PdfViewer url={fileUrl} title={doc.title} />
                </Suspense>
              )}
              {doc.preview_kind === "docx" && <DocxPreview url={fileUrl} />}
              {doc.preview_kind === "text" && <TextPreview url={fileUrl} />}
              {doc.preview_kind === "image" && (
                <div className="p-3 sm:p-6 bg-black/5 dark:bg-black/40 flex items-center justify-center">
                  <img src={fileUrl} alt={doc.title} className="max-w-full max-h-[75vh] rounded-md" data-testid="kb-viewer-image" />
                </div>
              )}
              {doc.preview_kind === "video" && <VideoPreview url={fileUrl} poster={doc.thumbnail_url} />}
              {doc.preview_kind === "youtube" && <YouTubePreview doc={doc} />}
              {doc.preview_kind === "legacy_doc" && (
                <CardContent className="pt-6">
                  <EmptyState icon={FileWarning} title="Preview unavailable for this legacy Word format"
                    hint="This is an older .doc file — SalesOS can't safely render it read-only in the browser."
                    action={<a href={fileUrl} target="_blank" rel="noreferrer"><Button size="sm"><Download className="h-3.5 w-3.5 mr-1.5" />Download</Button></a>} />
                </CardContent>
              )}
              {doc.preview_kind === "other" && (
                <CardContent className="pt-6">
                  <EmptyState icon={ExternalLink} title="No inline preview available for this file type"
                    action={<a href={fileUrl} target="_blank" rel="noreferrer"><Button size="sm"><Download className="h-3.5 w-3.5 mr-1.5" />Download</Button></a>} />
                </CardContent>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
